import { useCallback, useState } from 'react';
import { t } from 'i18next';
import { IXsollaPricingResponse, XSOLLA_ACTIVE_SUBSCRIPTION_ERROR_CODE } from '../../../common/xsolla';
import { IXsollaCheckoutError, toXsollaCheckoutError } from '../../../common/xsollaCheckoutError';
import { IXsollaWidgetPaymentInfo } from './xsollaWidgetAdapter';
import { useXsollaPricing } from './useXsollaPricing';
import { TXsollaCheckoutSelection } from './useXsollaCheckoutSession';

type TFetchXsollaPricing = (params: { market?: string }) => Promise<IXsollaPricingResponse & { success: boolean }>;

type TUseXsollaPremiumFlowOptions = {
  enabled: boolean;
  market?: string;
  fetchPricing: TFetchXsollaPricing;
  navigateToSuccessPage: (selection: TXsollaCheckoutSelection) => void;
  /** When provided, closing the payment widget offers a one-click "Resume checkout" action. */
  resumeCheckout?: (selection: TXsollaCheckoutSelection) => void;
};

export type TXsollaCheckoutPhase = 'idle' | 'starting' | 'pending' | 'closed' | 'error';

type TXsollaCheckoutFeedback = {
  phase: TXsollaCheckoutPhase;
  message: string | null;
  actionLabel: string | null;
  actionPath: string | null;
  actionRun: (() => void) | null;
};

const IDLE_FEEDBACK: TXsollaCheckoutFeedback = {
  phase: 'idle',
  message: null,
  actionLabel: null,
  actionPath: null,
  actionRun: null
};

export function useXsollaPremiumFlow(options: TUseXsollaPremiumFlowOptions) {
  const {
    market,
    enabled,
    fetchPricing,
    navigateToSuccessPage,
    resumeCheckout
  } = options;
  const [checkoutFeedback, setCheckoutFeedback] = useState<TXsollaCheckoutFeedback>(IDLE_FEEDBACK);
  const { pricing, pricingInfo, isPricingLoading, isPricingUnavailable } = useXsollaPricing({
    enabled,
    market,
    fetchPricing
  });

  const clearCheckoutFeedback = useCallback(() => {
    setCheckoutFeedback(IDLE_FEEDBACK);
  }, []);

  const onCheckoutStart = useCallback(() => {
    setCheckoutFeedback({ ...IDLE_FEEDBACK, phase: 'starting' });
  }, []);

  const onCheckoutSuccess = useCallback((_paymentInfo: IXsollaWidgetPaymentInfo, selection: TXsollaCheckoutSelection) => {
    setCheckoutFeedback(IDLE_FEEDBACK);

    navigateToSuccessPage(selection);
  }, [navigateToSuccessPage]);

  const onCheckoutPending = useCallback((_paymentInfo: IXsollaWidgetPaymentInfo, selection: TXsollaCheckoutSelection) => {
    setCheckoutFeedback({ ...IDLE_FEEDBACK, phase: 'pending', message: t('xsolla:payment-processing') });

    navigateToSuccessPage(selection);
  }, [navigateToSuccessPage]);

  const onCheckoutClose = useCallback((closedSelection?: TXsollaCheckoutSelection) => {
    const canResume = !!closedSelection && !!resumeCheckout;

    setCheckoutFeedback((current) => current.phase === 'pending'
      ? current
      : {
          ...IDLE_FEEDBACK,
          phase: 'closed',
          message: t('xsolla:checkout-closed'),
          actionLabel: canResume ? t('xsolla:resume-checkout') : null,
          // Widget-closers already picked a tier and opened payment — the
          // warmest recoverable segment, so hand them a one-click retry.
          actionRun: canResume ? () => resumeCheckout!(closedSelection!) : null
        });
  }, [resumeCheckout]);

  const onCheckoutError = useCallback((error: IXsollaCheckoutError | string) => {
    const normalizedError = typeof error === 'string'
      ? { message: error }
      : toXsollaCheckoutError(error) || error;

    console.error('Xsolla checkout error:', normalizedError);

    if (normalizedError.code === XSOLLA_ACTIVE_SUBSCRIPTION_ERROR_CODE) {
      setCheckoutFeedback({
        ...IDLE_FEEDBACK,
        phase: 'error',
        message: t('xsolla:active-subscription-guard-message'),
        actionLabel: t('xsolla:success.cta-subscriptions'),
        actionPath: '/settings/subscriptions'
      });
      return;
    }

    setCheckoutFeedback({
      ...IDLE_FEEDBACK,
      phase: 'error',
      message: normalizedError.message || t('common:errors.something-went-wrong')
    });
  }, []);

  return {
    pricing,
    pricingInfo,
    isPricingLoading,
    isPricingUnavailable,
    checkoutPhase: checkoutFeedback.phase,
    checkoutNotice: checkoutFeedback.message,
    checkoutNoticeActionLabel: checkoutFeedback.actionLabel,
    checkoutNoticeActionPath: checkoutFeedback.actionPath,
    checkoutNoticeActionRun: checkoutFeedback.actionRun,
    clearCheckoutFeedback,
    onCheckoutStart,
    onCheckoutSuccess,
    onCheckoutPending,
    onCheckoutClose,
    onCheckoutError
  };
}
